<template>
  <div class="social-login">
    <facebook-login
      v-if="showFacebookLogin"
      class="social-login__facebook"
      data-test-id="social-login-facebook"
      :button-text="facebookButtonText"
      @click="facebookLogIn()"
    />
    <google-login
      v-if="showGoogleLogin"
      class="social-login__google"
      data-test-id="social-login-google"
      :button-text-override="googleButtonText"
      @click="googleLogIn()"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FacebookLogin from '@/components/Social/FacebookLogin.vue';
import GoogleLogin from '@/components/Social/GoogleLogin.vue';
import { SET_CURRENT_USER } from '@/graphql/mutations/current-user-mutation';
import dataLayerMixin from '@/mixins/data-layer-mixin';
import authRedirectMixin from '@/mixins/auth-redirect-mixin';

export default {
  name: 'SocialLogin',
  components: {
    FacebookLogin,
    GoogleLogin,
  },
  mixins: [dataLayerMixin, authRedirectMixin],
  props: {
    accountProviders: {
      type: Array,
      default: () => [
        'facebook',
        'google',
      ],
    },
    isRegistrationPage: {
      type: Boolean,
      default: () => false,
    },
    googleButtonText: {
      type: String,
      default: () => '',
    },
    facebookButtonText: {
      type: String,
      default: () => '',
    },
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'siteHasFeature',
    ]),
    showGoogleLogin() {
      return this.accountProviders.includes('google');
    },
    showFacebookLogin() {
      return this.accountProviders.includes('facebook');
    },
    userHasFirstAndLastName() {
      return this.currentUser.fname && this.currentUser.lname;
    },
    nextStepForNewUser() {
      if (this.siteHasFeature('rare_disease')) {
        return 'requestDisorderDemographicInfo';
      }
      if (this.siteHasFeature('social_network') && !this.userHasFirstAndLastName) {
        return 'fNameAndLName';
      }
      return 'setUsername';
    },
  },
  methods: {
    googleLogIn() {
      this.$auth.googleLogIn(this.callback);
    },
    facebookLogIn() {
      this.$auth.facebookLogIn(this.callback);
    },
    callback(socialAccountType) {
      if (typeof (parentLoginPayload) !== 'undefined') {
        // eslint-disable-next-line no-undef
        const { user } = parentLoginPayload;
        this.$store.dispatch('setCurrentUser', user);
        this.$store.dispatch('setLoggedInFromSocial');

        this.setDataLayer();
        this.pushDataLayer();
        this.setTrackingInAC(user.email);

        this.$snowplow.connectSocialAccountTracking({
          data: {
            connected_account: socialAccountType,
          },
        });
        this.cleanUp();
      } else {
        this.$store.dispatch('checkForCurrentUser', this).then(this.cleanUp);
      }
    },
    cleanUp() {
      if (!this.$store.state.currentUser.loggedIn) { return; }

      const { username } = this.currentUser;
      const tempUsername = `CommunityMember${this.currentUser.id}`;
      const siteIsShn = this.siteHasFeature('social_network');

      if (!username) {
        this.$apollo.mutate({
          mutation: SET_CURRENT_USER,
          variables: {
            username: tempUsername,
          },
        }).then(({ data }) => {
          this.$store.dispatch('setCurrentUser', data.updateUser);
          if (this.siteHasFeature('social_network')) {
            this.$store.dispatch('setProgressIndicatorSteps', 4);
          }
          const action = this.$store.state.authOpen ? 'setAuthStep' : 'openAuthDialog';
          this.$store.dispatch(action, { authStep: this.nextStepForNewUser });
        });
      } else if (username === tempUsername && !siteIsShn) {
        const action = this.$store.state.authOpen ? 'setAuthStep' : 'openAuthDialog';
        this.$store.dispatch(action, { authStep: this.nextStepForNewUser });
      } else {
        this.handleLoginRedirect();

        this.$store.dispatch('addToastNotification', {
          toastType: 'success',
          description: `Welcome back ${username}!`,
        });

        const userHasNeverBeenLoggedInBeforeThis = !this.$store.getters.currentUser.lastLoginAt;
        if (siteIsShn) {
          if (userHasNeverBeenLoggedInBeforeThis) {
            this.$store.dispatch('setFirstTimeLoggingIntoSHN', true);
            this.$store.dispatch('openAuthDialog', { authStep: 'setUsername' });
            return;
          }

          if (!this.currentUser.hasDisorder) {
            this.$store.dispatch('openAuthDialog', { authStep: 'requestDisorderDemographicInfo' });
            return;
          }

          if (!this.currentUser.hasSocialSite) {
            this.$store.dispatch('openAuthDialog', { authStep: 'setSocialSites' });
            return;
          }
        }

        this.$store.dispatch('closeAuthDialog');
      }
    },
  },
};

</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_social-login';
</style>

<docs>

Buttons for social login / register with Google and Facebook

</docs>
